/**
 * Серверы Telegram — узлы Telegram Service.
 *
 * ── Что решает панель ───────────────────────────────────────────────────────
 *
 * Узлы заводились только запросом к API: администратор не видел ни где живёт
 * какой аккаунт, ни отвечает ли сервер. Панель показывает полную картину —
 * ядро и узлы, состояние каждого прямо сейчас, загрузку против потолка — и
 * даёт делать то же, что API, без curl.
 *
 * ── Перенос аккаунта — с предупреждением ───────────────────────────────────
 *
 * Перенос меняет только запись в базе. Каталог TDLib с сессией лежит на
 * старом сервере, и его копируют руками при остановленном сервисе
 * (docs/DEPLOYMENT.md). Перенос без копирования — это разлогиненный рабочий
 * аккаунт и «вход с нового устройства» в глазах Telegram. Поэтому кнопка
 * спрашивает подтверждение и называет последствие, а не «вы уверены».
 *
 * ── Состояние — отдельным запросом ──────────────────────────────────────────
 *
 * Опрос узлов может занять секунды (выключенный сервер ждём до таймаута).
 * Таблица приходит сразу, точки состояния — когда опрос закончится.
 */

import { useCallback, useEffect, useState } from "react";
import type { AnyTelegramAccountDto } from "@tw/shared-types";
import { useSession } from "../state/session.tsx";
import type {
  TelegramNodeDto,
  TelegramNodeHealthDto,
  TelegramNodesDto,
} from "../lib/api.js";
import { describeError } from "../lib/errors.js";
import {
  ADMIN_HINT,
  ADMIN_PANEL,
  ADMIN_PANEL_TITLE,
  ADMIN_SUBTITLE,
} from "./adminPanel.js";
import {
  ALERT,
  BUTTON_DANGER,
  BUTTON_GHOST,
  BUTTON_PRIMARY,
  FIELD,
  TAG,
} from "./controls.js";
import { accountLabel, accountSubtitle } from "./accountLabel.ts";
import { PlusIcon, RefreshIcon, TrashIcon } from "./icons.tsx";
import { plural } from "./stays/meta.ts";
import { cn } from "@/lib/utils";

/** Ключ ядра — так его называет сервер. */
const CORE = "core";

const STATE_TEXT: Record<TelegramNodeHealthDto["state"], string> = {
  ok: "Работает",
  token: "Чужой токен — запросы отклоняются",
  unexpected: "Отвечает, но не как Telegram Service",
  down: "Не отвечает",
};

const STATE_DOT: Record<TelegramNodeHealthDto["state"], string> = {
  ok: "text-dot-done",
  token: "text-dot-lost",
  unexpected: "text-dot-progress",
  down: "text-dot-lost",
};

/** Точка подключения аккаунта — те же цвета, что у состояния сервера. */
const CONNECTION_DOT: Record<string, string> = {
  CONNECTED: "text-dot-done",
  RECONNECTING: "text-dot-progress",
  AUTHORIZATION_REQUIRED: "text-dot-progress",
  ERROR: "text-dot-lost",
};

/** Подсказка, что делать, — только для плохих состояний. */
const STATE_ADVICE: Partial<Record<TelegramNodeHealthDto["state"], string>> = {
  token:
    "У сервиса на этом сервере другой TELEGRAM_SERVICE_TOKEN. Он должен " +
    "совпадать с ядром, иначе аккаунты на узле не работают.",
  unexpected:
    "По адресу отвечает что-то другое: проверьте порт и путь — нужен " +
    "внутренний адрес сервиса, например http://10.0.0.5:4100.",
  down:
    "Сервер не ответил за 3 секунды. Проверьте, что он включён и сервис " +
    "запущен, и что ядро видит его по внутренней сети.",
};

interface Props {
  accounts: AnyTelegramAccountDto[];
  /** Аккаунты переехали — пусть экран перечитает список. */
  onChanged: () => void;
}

export function TelegramNodesPanel({ accounts, onChanged }: Props) {
  const { api } = useSession();

  const [data, setData] = useState<TelegramNodesDto | null>(null);
  const [health, setHealth] = useState<Map<string, TelegramNodeHealthDto>>(
    new Map(),
  );
  const [checking, setChecking] = useState(false);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.telegramNodes());
    } catch (cause) {
      setError(describeError(cause));
    }
  }, [api]);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      const { items } = await api.telegramNodesHealth();
      setHealth(new Map(items.map((item) => [item.key, item])));
      setCheckedAt(new Date());
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setChecking(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
    void check();
  }, [load, check]);

  /** Действие с перечитыванием таблицы и, если надо, состояния. */
  const act = async (
    fn: () => Promise<unknown>,
    options: { recheck?: boolean } = {},
  ): Promise<boolean> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await load();
      if (options.recheck) void check();
      return true;
    } catch (cause) {
      setError(describeError(cause));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const move = (account: AnyTelegramAccountDto, target: { id: string | null; label: string }) => {
    const ok = window.confirm(
      `Перенести «${accountLabel(account)}» на «${target.label}»?\n\n` +
        "Меняется только запись в базе. Файлы сессии Telegram (каталог TDLib) " +
        "нужно заранее скопировать на новый сервер при остановленном сервисе — " +
        "иначе аккаунт выйдет из Telegram и потребует вход заново, а Telegram " +
        "увидит вход с нового устройства.\n\nКаталог уже скопирован?",
    );
    if (!ok) return;

    void act(async () => {
      const result = await api.moveTelegramAccount(account.id, target.id);
      setNotice(result.warning);
      onChanged();
    });
  };

  if (!data) {
    return (
      <section className={ADMIN_PANEL}>
        {error ? (
          <p role="alert" className={ALERT}>{error}</p>
        ) : (
          <p className="text-sm text-muted-foreground">Загрузка…</p>
        )}
      </section>
    );
  }

  const byId = new Map(accounts.map((account) => [account.id, account]));
  const accountsOn = (nodeId: string | null): AnyTelegramAccountDto[] =>
    data.placement
      .filter((p) => p.nodeId === nodeId)
      .map((p) => byId.get(p.telegramAccountId))
      .filter((a): a is AnyTelegramAccountDto => Boolean(a))
      .sort((a, b) => accountLabel(a).localeCompare(accountLabel(b), "ru"));

  /** Куда можно перенести: все, кроме текущего. Выключенные — тоже, это решение админа. */
  const targets: Array<{ id: string | null; label: string }> = [
    { id: null, label: data.core.label },
    ...data.items.map((node) => ({ id: node.id, label: node.label })),
  ];

  return (
    <div className="flex flex-col gap-5">
      <section className={ADMIN_PANEL}>
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className={ADMIN_PANEL_TITLE}>Серверы Telegram</h2>
            <p className={cn(ADMIN_HINT, "mt-1 max-w-[70ch]")}>
              Каждый подключённый аккаунт держит переписку в памяти сервера.
              Когда памяти ядра перестаёт хватать, сервис Telegram ставят на
              отдельный сервер — узел — и новые аккаунты селятся туда сами:
              на наименее загруженный включённый узел со свободным местом.
            </p>
          </div>

          <div className="flex flex-none items-center gap-2">
            {checkedAt && !checking && (
              <span className="text-[12px] text-muted-foreground">
                Проверено в{" "}
                {checkedAt.toLocaleTimeString("ru", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            )}
            <button
              type="button"
              className={cn(BUTTON_GHOST, "inline-flex items-center gap-1.5")}
              disabled={checking}
              onClick={() => void check()}
            >
              <RefreshIcon className={cn("size-3.5", checking && "animate-spin")} />
              {checking ? "Проверяю…" : "Проверить"}
            </button>
          </div>
        </div>
      </section>

      {error && <p role="alert" className={ALERT}>{error}</p>}
      {notice && (
        <p className="rounded-xl border border-dot-progress/40 bg-dot-progress/10 px-3 py-2 text-sm text-heading">
          {notice}
        </p>
      )}

      <NodeCard
        title={data.core.label}
        nodeKey={CORE}
        baseUrl={data.core.baseUrl}
        enabled
        capacity={0}
        count={data.core.accounts}
        health={health.get(CORE)}
        checking={checking}
        accounts={accountsOn(null)}
        targets={targets.filter((t) => t.id !== null)}
        busy={busy}
        onMove={move}
        note="Встроен в основную установку. Его нельзя выключить или удалить; аккаунты, которых никуда не переносили, живут здесь."
      />

      {data.items.map((node) => (
        <NodeCard
          key={node.id}
          node={node}
          title={node.label}
          nodeKey={node.key}
          baseUrl={node.baseUrl}
          enabled={node.enabled}
          capacity={node.capacity}
          count={node.accounts}
          health={health.get(node.key)}
          checking={checking}
          accounts={accountsOn(node.id)}
          targets={targets.filter((t) => t.id !== node.id)}
          busy={busy}
          onMove={move}
          onToggle={() =>
            void act(() =>
              api.updateTelegramNode(node.id, { enabled: !node.enabled }),
            )
          }
          onSave={(values) =>
            act(() => api.updateTelegramNode(node.id, values), {
              recheck: values.baseUrl !== undefined,
            })
          }
          onDelete={() => {
            const ok = window.confirm(
              `Удалить сервер «${node.label}» из списка?\n\n` +
                "Сам сервер и его сервис не останавливаются — он просто " +
                "перестанет использоваться.",
            );
            if (ok) void act(() => api.deleteTelegramNode(node.id), { recheck: true });
          }}
        />
      ))}

      <NewNodeForm
        busy={busy}
        onCreate={(values) =>
          act(() => api.createTelegramNode(values), { recheck: true })
        }
      />
    </div>
  );
}

// ── Карточка сервера ─────────────────────────────────────────────────────────

interface CardProps {
  /** Нет у ядра: его нельзя ни править, ни выключать. */
  node?: TelegramNodeDto;
  title: string;
  nodeKey: string;
  baseUrl: string;
  enabled: boolean;
  capacity: number;
  count: number;
  health: TelegramNodeHealthDto | undefined;
  checking: boolean;
  accounts: AnyTelegramAccountDto[];
  targets: Array<{ id: string | null; label: string }>;
  busy: boolean;
  note?: string;
  onMove: (account: AnyTelegramAccountDto, target: { id: string | null; label: string }) => void;
  onToggle?: () => void;
  onSave?: (values: { label?: string; baseUrl?: string; capacity?: number }) => Promise<boolean>;
  onDelete?: () => void;
}

function NodeCard(props: CardProps) {
  const { node, health, checking, accounts, count, capacity } = props;
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);

  const full = capacity > 0 && count >= capacity;
  const share = capacity > 0 ? Math.min(1, count / capacity) : 0;

  return (
    <section className={cn(ADMIN_PANEL, !props.enabled && "opacity-80")}>
      <div className="flex flex-wrap items-center gap-2">
        <span
          aria-hidden
          className={cn(
            "status-dot",
            health ? STATE_DOT[health.state] : "text-muted-foreground",
            checking && !health && "animate-pulse",
          )}
        />
        <h3 className={ADMIN_SUBTITLE}>{props.title}</h3>
        <span className={cn(TAG, "font-mono text-[11px]")}>{props.nodeKey}</span>
        {!props.enabled && (
          <span className={cn(TAG, "text-[11px] text-muted-foreground")}>
            выключен — новые аккаунты не получает
          </span>
        )}

        {node && (
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              className={BUTTON_GHOST}
              disabled={props.busy}
              onClick={() => setEditing((v) => !v)}
            >
              {editing ? "Отмена" : "Изменить"}
            </button>
            <button
              type="button"
              className={BUTTON_GHOST}
              disabled={props.busy}
              onClick={props.onToggle}
              title={
                props.enabled
                  ? "Новые аккаунты сюда не попадут; уже подключённые продолжат работать"
                  : undefined
              }
            >
              {props.enabled ? "Выключить" : "Включить"}
            </button>
            <button
              type="button"
              className={cn(BUTTON_DANGER, "grid size-8 place-items-center p-0")}
              aria-label={`Удалить сервер ${props.title}`}
              disabled={props.busy || count > 0}
              title={count > 0 ? "Сначала перенесите аккаунты с этого сервера" : undefined}
              onClick={props.onDelete}
            >
              <TrashIcon className="size-4" />
            </button>
          </div>
        )}
      </div>

      <p className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
        <span className={health?.state === "ok" ? "text-heading" : "text-muted-foreground"}>
          {health ? STATE_TEXT[health.state] : checking ? "Проверяю…" : "Не проверено"}
        </span>
        {health?.latencyMs != null && (
          <span className="text-muted-foreground">· {health.latencyMs} мс</span>
        )}
        {health?.driver && health.driver !== "tdlib" && (
          <span className="text-muted-foreground">· драйвер {health.driver}</span>
        )}
      </p>
      {health && STATE_ADVICE[health.state] && (
        <p className="text-[12px] leading-relaxed text-muted-foreground">
          {STATE_ADVICE[health.state]}
        </p>
      )}

      <p className="truncate font-mono text-[11px] text-muted-foreground">{props.baseUrl}</p>

      {/* Загрузка. Полоска только при заданном потолке: без него делить не на что. */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[13px] text-heading">
          {count} {plural(count, "аккаунт", "аккаунта", "аккаунтов")}
          {capacity > 0 ? ` из ${capacity}` : ""}
          {capacity === 0 && node && (
            <span className="text-muted-foreground"> · без ограничения</span>
          )}
          {full && <span className="text-destructive"> · заполнен</span>}
        </span>
        {capacity > 0 && (
          <div className="h-1.5 w-full max-w-[320px] overflow-hidden rounded-full bg-secondary">
            <div
              className={cn("h-full rounded-full", full ? "bg-destructive" : "bg-brand")}
              style={{ width: `${Math.round(share * 100)}%` }}
            />
          </div>
        )}
      </div>

      {props.note && <p className={ADMIN_HINT}>{props.note}</p>}

      {editing && node && props.onSave && (
        <NodeEditor
          node={node}
          busy={props.busy}
          onSave={async (values) => {
            if (await props.onSave!(values)) setEditing(false);
          }}
        />
      )}

      {accounts.length > 0 && (
        <div>
          <button
            type="button"
            className="border-transparent bg-transparent p-0 text-[12px] font-medium text-brand hover:underline"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? "Скрыть аккаунты" : "Показать аккаунты"}
          </button>

          {open && (
            <ul className="mt-2 divide-y divide-border rounded-xl border border-border">
              {accounts.map((account) => (
                <AccountRow
                  key={account.id}
                  account={account}
                  targets={props.targets}
                  busy={props.busy}
                  onMove={props.onMove}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function AccountRow({
  account,
  targets,
  busy,
  onMove,
}: {
  account: AnyTelegramAccountDto;
  targets: Array<{ id: string | null; label: string }>;
  busy: boolean;
  onMove: CardProps["onMove"];
}) {
  const subtitle = accountSubtitle(account);

  return (
    <li className="flex flex-wrap items-center gap-2 px-3 py-2">
      <span
        aria-hidden
        className={cn(
          "status-dot",
          CONNECTION_DOT[account.connectionState] ?? "text-muted-foreground",
        )}
      />
      <span className="min-w-0 flex-1 truncate text-[13px] text-heading">
        {accountLabel(account)}
        {subtitle && <span className="ml-2 text-muted-foreground">{subtitle}</span>}
      </span>

      {targets.length > 0 && (
        <select
          className={cn(FIELD, "h-8 w-auto text-[12px]")}
          aria-label={`Перенести ${accountLabel(account)}`}
          disabled={busy}
          value=""
          onChange={(e) => {
            const target = targets.find((t) => (t.id ?? CORE) === e.target.value);
            if (target) onMove(account, target);
          }}
        >
          <option value="" disabled>
            Перенести на…
          </option>
          {targets.map((t) => (
            <option key={t.id ?? CORE} value={t.id ?? CORE}>
              {t.label}
            </option>
          ))}
        </select>
      )}
    </li>
  );
}

// ── Формы ────────────────────────────────────────────────────────────────────

function NodeEditor({
  node,
  busy,
  onSave,
}: {
  node: TelegramNodeDto;
  busy: boolean;
  onSave: (values: { label?: string; baseUrl?: string; capacity?: number }) => void;
}) {
  const [label, setLabel] = useState(node.label);
  const [baseUrl, setBaseUrl] = useState(node.baseUrl);
  const [capacity, setCapacity] = useState(String(node.capacity));

  // Отправляем только изменённое: аудит пишет правку как есть, и «изменил
  // название» не должно выглядеть как «изменил адрес».
  const changes: { label?: string; baseUrl?: string; capacity?: number } = {};
  if (label.trim() !== node.label) changes.label = label.trim();
  if (baseUrl.trim() !== node.baseUrl) changes.baseUrl = baseUrl.trim();
  if (Number(capacity) !== node.capacity) changes.capacity = Number(capacity);

  return (
    <form
      className="grid gap-2 rounded-xl border border-border bg-secondary/40 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(changes);
      }}
    >
      <NodeFields
        label={label}
        setLabel={setLabel}
        baseUrl={baseUrl}
        setBaseUrl={setBaseUrl}
        capacity={capacity}
        setCapacity={setCapacity}
      />
      {changes.baseUrl !== undefined && node.accounts > 0 && (
        <p className="text-[12px] leading-relaxed text-destructive">
          Новый адрес должен вести на тот же сервер с теми же файлами сессий —
          иначе {node.accounts} {plural(node.accounts, "аккаунт", "аккаунта", "аккаунтов")} потребуют вход заново.
        </p>
      )}
      <div>
        <button
          type="submit"
          className={BUTTON_PRIMARY}
          disabled={busy || Object.keys(changes).length === 0 || !label.trim() || !baseUrl.trim()}
        >
          Сохранить
        </button>
      </div>
    </form>
  );
}

function NewNodeForm({
  busy,
  onCreate,
}: {
  busy: boolean;
  onCreate: (values: { key: string; label: string; baseUrl: string; capacity: number }) => Promise<boolean>;
}) {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [capacity, setCapacity] = useState("10");

  const ready = key.trim().length >= 2 && label.trim() && baseUrl.trim();

  return (
    <section className={ADMIN_PANEL}>
      <h2 className={ADMIN_PANEL_TITLE}>Новый сервер</h2>
      <p className={ADMIN_HINT}>
        Сначала поднимите на сервере сервис Telegram с тем же
        TELEGRAM_SERVICE_TOKEN, что у ядра (docs/DEPLOYMENT.md), и откройте его
        только для внутренней сети. Потом добавьте его здесь — проверка сразу
        покажет, видит ли ядро новый сервер.
      </p>

      <form
        className="mt-1 grid gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void onCreate({
            key: key.trim(),
            label: label.trim(),
            baseUrl: baseUrl.trim(),
            capacity: Number(capacity) || 0,
          }).then((ok) => {
            if (!ok) return;
            setKey("");
            setLabel("");
            setBaseUrl("");
            setCapacity("10");
          });
        }}
      >
        <label className="grid gap-1 text-xs font-medium text-muted-foreground">
          Ключ
          <input
            className={cn(FIELD, "font-mono")}
            value={key}
            onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
            placeholder="node-1"
            maxLength={32}
          />
          <span className="text-[11px] font-normal leading-relaxed text-muted-foreground">
            Латиница, цифры и дефис. Не меняется — по нему сервер виден в журнале.
          </span>
        </label>

        <NodeFields
          label={label}
          setLabel={setLabel}
          baseUrl={baseUrl}
          setBaseUrl={setBaseUrl}
          capacity={capacity}
          setCapacity={setCapacity}
        />

        <button
          type="submit"
          className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-full border-transparent bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-45"
          disabled={busy || !ready}
        >
          <PlusIcon className="size-4" />
          Добавить сервер
        </button>
      </form>
    </section>
  );
}

function NodeFields(props: {
  label: string;
  setLabel: (v: string) => void;
  baseUrl: string;
  setBaseUrl: (v: string) => void;
  capacity: string;
  setCapacity: (v: string) => void;
}) {
  return (
    <>
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_140px]">
        <label className="grid gap-1 text-xs font-medium text-muted-foreground">
          Название
          <input
            className={FIELD}
            value={props.label}
            onChange={(e) => props.setLabel(e.target.value)}
            placeholder="Сервер 2 — Варшава"
            maxLength={64}
          />
        </label>
        <label className="grid gap-1 text-xs font-medium text-muted-foreground">
          Внутренний адрес
          <input
            className={cn(FIELD, "font-mono")}
            value={props.baseUrl}
            onChange={(e) => props.setBaseUrl(e.target.value)}
            placeholder="http://10.0.0.5:4100"
            maxLength={2048}
          />
        </label>
        <label className="grid gap-1 text-xs font-medium text-muted-foreground">
          Потолок
          <input
            className={FIELD}
            type="number"
            min={0}
            max={1000}
            value={props.capacity}
            onChange={(e) => props.setCapacity(e.target.value)}
          />
        </label>
      </div>
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Потолок — сколько аккаунтов держит сервер; 0 — без ограничения.
        Ориентир: около 10 аккаунтов на 2 ГБ памяти.
      </p>
    </>
  );
}
